/**
 * HenaketDatePicker (0.35.54) — custom date input replacing native
 * <input type="date">.
 *
 * Visual spec from the user:
 *   • Working days: black text (--color-text-800)
 *   • Non-working days: red text (--color-error). For v1 "non-working"
 *     means weekend (Sat + Sun, Armenia's convention).
 *   • Selected day: magenta text + magenta border (--color-focus,
 *     a.k.a. Henaket purple). Bold.
 *   • Large cells (48px square) — easy to see and click.
 *
 * Future configs (to be added per user request): multi-select,
 * disabling past dates, custom min/max, custom non-working-day
 * patterns (e.g. mark certain weekdays as working, mark national
 * holidays as non-working), date-range selection.
 *
 * API:
 *   value:        'YYYY-MM-DD' string (matches native date input) or ''
 *   onChange:     (newValue) => void
 *   label:        string — rendered as <label> above the trigger
 *   required:     bool
 *   requiredPosition: 'before' | 'after' (where the * sits)
 *   disabled:     bool
 *   error:        string — renders the global .field-error-text + bar
 *   name:         hidden-input form-field name (form submit compatibility)
 *   placeholder:  shown in the trigger when no date is selected
 *   id:           DOM id for the trigger (and `for` of the label)
 */
import { useState, useRef, useEffect, useCallback, useId, Fragment } from 'react';
import { useOptionalLanguage } from '../context/LanguageContext';
import { fetchLibrary, getCachedLibrary, subscribeLibraryCache } from '../lib/libraryCache';
import { getSystemDateFormat } from '../utils/format';
import { useFormatVersion } from '../hooks/useFormatVersion';
import styles from './HenaketDatePicker.module.css';

/* 0.44.63 - Look rebuilt to the Henaket date picker specification
 * (segmented Day / Month / Year fields with labels above, slash
 * separators, a 40px calendar button; a 316px popover with
 * << < title > >> and a single-pixel day lattice, Monday first).
 *
 * Accessibility fixes the specification asked for:
 *   - the control is a role="group" named by its label;
 *   - the day grid has a roving tabindex: one day is in the tab order,
 *     arrows move by day / week, PageUp / PageDown by month, Home /
 *     End to the ends of the week, Enter / Space pick;
 *   - focus moves into the popover when it opens and back to the
 *     calendar button when it closes from the keyboard;
 *   - the month and year title is announced (aria-live) when it changes.
 *
 * 0.44.68 - Month and weekday names, abbreviations and the Day / Month
 * / Year labels come from the built-in library "Months and days"
 * (months_and_days, edited in admin > Libraries), in the page's
 * language. The button names, read by screen readers only, and the
 * defaults used until the library has loaded, are built in below.
 * `lang` forces a language (admin screens pass 'en').
 */
const DEFAULT_STRINGS = {
  hy: {
    day: 'Օր', month: 'Ամիս', year: 'Տարի',
    open_calendar: 'Բացել օրացույցը', calendar: 'Օրացույց',
    prev_year: 'Նախորդ տարի', prev_month: 'Նախորդ ամիս',
    next_month: 'Հաջորդ ամիս', next_year: 'Հաջորդ տարի',
    choose_year: 'Ընտրել տարին', prev_year_page: 'Նախորդ 12 տարիները', next_year_page: 'Հաջորդ 12 տարիները',
    month_names: 'Հունվար, Փետրվար, Մարտ, Ապրիլ, Մայիս, Հունիս, Հուլիս, Օգոստոս, Սեպտեմբեր, Հոկտեմբեր, Նոյեմբեր, Դեկտեմբեր',
    weekday_names: 'Երկ, Երք, Չրք, Հնգ, Ուր, Շբթ, Կիր',
  },
  ru: {
    day: 'День', month: 'Месяц', year: 'Год',
    open_calendar: 'Открыть календарь', calendar: 'Календарь',
    prev_year: 'Предыдущий год', prev_month: 'Предыдущий месяц',
    next_month: 'Следующий месяц', next_year: 'Следующий год',
    choose_year: 'Выбрать год', prev_year_page: 'Предыдущие 12 лет', next_year_page: 'Следующие 12 лет',
    month_names: 'Январь, Февраль, Март, Апрель, Май, Июнь, Июль, Август, Сентябрь, Октябрь, Ноябрь, Декабрь',
    weekday_names: 'Пн, Вт, Ср, Чт, Пт, Сб, Вс',
  },
  en: {
    day: 'Day', month: 'Month', year: 'Year',
    open_calendar: 'Open calendar', calendar: 'Calendar',
    prev_year: 'Previous year', prev_month: 'Previous month',
    next_month: 'Next month', next_year: 'Next year',
    choose_year: 'Choose a year', prev_year_page: 'Previous 12 years', next_year_page: 'Next 12 years',
    month_names: 'January, February, March, April, May, June, July, August, September, October, November, December',
    weekday_names: 'Mon, Tue, Wed, Thu, Fri, Sat, Sun',
  },
};

/* 0.44.64 - The year picker shows 12 years (3 x 4), in fixed pages
 * aligned to 2016-2027 (the design's page), so a year always lands on
 * the same page. */
const YEARS_PER_PAGE = 12;
const yearPageStart = (y) => 2016 + YEARS_PER_PAGE * Math.floor((y - 2016) / YEARS_PER_PAGE);

function useDatePickerStrings(forcedLang) {
  const ctx = useOptionalLanguage();
  const lang = forcedLang || (ctx && ctx.language) || 'en';
  const defaults = DEFAULT_STRINGS[lang] || DEFAULT_STRINGS.en;
  // 0.44.68 - The library's rows, in the language (cached and shared).
  const [rows, setRows] = useState(() => {
    const c = getCachedLibrary('months_and_days', lang);
    return c && Array.isArray(c.rows) ? c.rows : null;
  });
  useEffect(() => {
    let off = false;
    const take = () => {
      const c = getCachedLibrary('months_and_days', lang);
      if (!off && c && Array.isArray(c.rows)) setRows(c.rows);
    };
    take();
    fetchLibrary('months_and_days', lang).then(take).catch(() => { /* keep the defaults */ });
    const unsub = subscribeLibraryCache(take);
    return () => { off = true; if (typeof unsub === 'function') unsub(); };
  }, [lang]);
  const byKind = (kind) => (rows || []).filter(r => r && r.kind === kind)
    .sort((x, y) => Number(x.number) - Number(y.number));
  const pick = (list, col, n, fallbackKey) => {
    const vals = list.map(r => String(r[col] || '').trim());
    const fb = String(defaults[fallbackKey]).split(',').map(x => x.trim());
    return list.length === n ? vals.map((v, i) => v || fb[i]) : fb;
  };
  const fields = Object.fromEntries(byKind('field').map(r => [r.code, String(r.name || '').trim()]));
  const tr = (k) => {
    if (k === 'day' && fields.f_day) return fields.f_day;
    if (k === 'month' && fields.f_month) return fields.f_month;
    if (k === 'year' && fields.f_year) return fields.f_year;
    return defaults[k];
  };
  return {
    tr,
    months: pick(byKind('month'), 'name', 12, 'month_names'),
    weekdaysMonFirst: pick(byKind('weekday'), 'short', 7, 'weekday_names'),
  };
}

// --- pure helpers (no React) ---

/** Format a Date as 'YYYY-MM-DD' in the LOCAL timezone (the same
 *  format <input type="date"> emits). */
function toIsoDateString(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse 'YYYY-MM-DD' to a local-midnight Date. Returns null for
 *  empty/invalid input. Anchoring at midnight avoids UTC-shift bugs
 *  where 'YYYY-MM-DD' might be interpreted as UTC and shift the day
 *  by one in some timezones. */
function fromIsoDateString(s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function sameDay(a, b) {
  return a && b
      && a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

/** Index of `d` within a week that starts on `weekStart`.
 *  weekStart: 0-6 (0=Sun, 1=Mon, ..., 6=Sat), matching JS getDay().
 *  Returns 0 if d falls on weekStart, 1 if the next day, ..., 6 if
 *  the day before weekStart. */
function weekdayIndex(d, weekStart) {
  return (d.getDay() - weekStart + 7) % 7;
}

/* 0.35.71 — isWeekend / isHoliday / isNonWorking helpers were
 * collapsed into a single `isNonWorking` method on the component
 * (closure over multiple Sets). See the component body. */

/** 0.44.63 - The month as the specification draws it: leading blank
 *  cells up to the first day's weekday column, then the month's days.
 *  No days from neighbouring months. */
function buildGrid(viewYear, viewMonth, weekStart) {
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const leadIn = weekdayIndex(firstOfMonth, weekStart);
  const days = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < leadIn; i++) cells.push({ blank: true });
  for (let dd = 1; dd <= days; dd++) {
    cells.push({ date: new Date(viewYear, viewMonth, dd), inMonth: true });
  }
  return cells;
}

// --- component ---

export default function HenaketDatePicker({
  id,
  label,
  value,
  onChange,
  required,
  requiredPosition = 'before',
  // 0.44.74 - No bottom margin of its own: the page's field wrapper
  // already spaces it (both together gave 32px below a date field).
  flush = false,
  disabled,
  error,
  name,
  placeholder = 'Select a date',
  // List of 'YYYY-MM-DD' strings to render in the non-working color.
  // Combined with weekends — a holiday on a weekday becomes
  // non-working just like a weekend day.
  holidays = [],
  // First column of the week. 0-6 (0=Sun, 1=Mon, ..., 6=Sat) matching
  // JS getDay(). Default 1 (Monday) for Armenia / ISO 8601.
  weekStart = 1,
  // 0.35.63 — non-working day text color. Defaults to
  // var(--color-text-500). Accepts any CSS color string. The future
  // form-editor date-element config will set this from a picker.
  nonWorkingColor,
  // 0.35.63 — Specific dates to render as DISABLED (very light gray,
  // not clickable, Enter on them is a no-op). Combined with the
  // min/max range bounds below — a date is disabled if it appears
  // in this list OR falls outside [minDate, maxDate]. Strings are
  // 'YYYY-MM-DD' format.
  disabledDates = [],
  minDate,            // 'YYYY-MM-DD' inclusive lower bound, or undefined
  maxDate,            // 'YYYY-MM-DD' inclusive upper bound, or undefined

  // 0.35.71 — Which weekdays are non-working. Array of 0-6 ints
  // matching JS getDay() (0=Sun, ..., 6=Sat). Default Sat+Sun per
  // Armenia / most jurisdictions. Empty array = no weekday is
  // non-working by default (a 7-day workweek), in which case only
  // holidays + nonWorkingOverrides drive the non-working state.
  nonWorkingDays = [0, 6],

  // 0.35.71 — Exception lists: specific dates that flip the
  // non-working state.
  //   • workingOverrides: ['YYYY-MM-DD']  — these dates ARE working
  //     even if they're a weekend, holiday, or nonWorkingOverride.
  //     Working-override wins over everything else (admin
  //     decision: if a date is listed here, treat it as working).
  //   • nonWorkingOverrides: ['YYYY-MM-DD']  — these dates ARE
  //     non-working even though they're weekdays and not in
  //     holidays. Use case: compensatory day-off when a holiday
  //     was moved.
  // Common pattern: a national holiday lands on Sunday → admin
  // adds Sunday to holidays AND adds the following Monday to
  // nonWorkingOverrides. (Or simpler: just add the Monday to
  // holidays directly. Both work; overrides are the cleaner
  // semantic when the day-off is compensatory rather than the
  // actual holiday.)
  workingOverrides = [],
  nonWorkingOverrides = [],
  // 0.35.97 — Disabled date ranges. Array of {start, end} pairs with
  // ISO 'YYYY-MM-DD' strings, both inclusive. Any date that falls
  // within ANY range becomes disabled (not selectable, rendered with
  // .dayDisabled style — same visual as out-of-min/max dates).
  // Use case: admin marks vacation windows, fully-booked periods,
  // or any other "really not selectable" blackout periods.
  disabledRanges = [],
  // 0.44.63 - Force the language of the picker's own words (labels,
  // month and weekday names). Admin screens pass 'en'; elsewhere the
  // site's current language is used.
  lang,
}) {
  const [open, setOpen] = useState(false);
  const { tr, months, weekdaysMonFirst } = useDatePickerStrings(lang);
  // 0.44.65 - The boxes follow the platform date format (Settings > Time
  // & date): Day / Month / Year for dd.mm.yyyy and dd/mm/yyyy, Month /
  // Day / Year for mm/dd/yyyy, Year / Month / Day for yyyy-mm-dd.
  useFormatVersion();
  const sysFmt = getSystemDateFormat() || 'dd.mm.yyyy';
  // 0.44.69 - Month first for mm/dd/yyyy and the "Oct 03, 2026" formats.
  const fmtLower = sysFmt.toLowerCase();
  const partOrder = fmtLower.startsWith('m') ? ['month', 'day', 'year']
    : fmtLower.startsWith('y') ? ['year', 'month', 'day'] : ['day', 'month', 'year'];
  const autoId = useId();
  const baseId = id || `dp-${autoId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const labelId = `${baseId}-label`;
  const calendarBtnRef = useRef(null);
  const panelRef = useRef(null);
  // Set when the popover opens: the next render moves focus into it.
  const wantFocusRef = useRef(false);

  // 0.36.37 — Structured DD / MM / YYYY input state.
  // The trigger is no longer a single button — it's three text
  // inputs (day, month, year) plus a calendar icon button at the
  // end. Each field carries its own string state (raw text the user
  // is typing). When all three parse to a valid date on blur, we
  // call onChange(isoString) so the parent picks it up. Calendar
  // pick flows the other way: selectedDate populates the three
  // input strings.
  const [dayInput, setDayInput] = useState('');
  const [monthInput, setMonthInput] = useState('');
  const [yearInput, setYearInput] = useState('');
  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  // viewDate: the month/year currently displayed in the panel.
  // Tracked independently of `value` so the user can flip through
  // months without committing.
  const [viewDate, setViewDate] = useState(() => {
    const parsed = fromIsoDateString(value);
    return parsed || new Date();
  });
  // 'days'   → standard calendar (default)
  // 'years'  → year picker (20-year grid). Click "January 2026"
  //             header. ←/→ pages by 20 years.
  // 'months' → month picker (12-month grid). Reached by picking
  //             a year in the year picker. ←/→ pages by 1 year.
  //             Picking a month returns to 'days' at that year+month.
  const [viewMode, setViewMode] = useState('days');
  // In 'years' mode: the year shown in the top-left cell of the
  // 20-year grid. null when not in years mode.
  const [yearViewStart, setYearViewStart] = useState(null);
  // 0.35.62 — Keyboard navigation state for day mode.
  const [keyboardDate, setKeyboardDate] = useState(null);
  // 0.35.62 — Keyboard navigation state for year mode (integer year).
  const [keyboardYear, setKeyboardYear] = useState(null);
  // 0.35.69 — Keyboard navigation state for month mode (Date object,
  // first-of-month). Tracks both the highlighted month AND the year
  // it belongs to (arrow keys can cross year boundaries).
  const [keyboardMonth, setKeyboardMonth] = useState(null);

  const containerRef = useRef(null);

  const selectedDate = fromIsoDateString(value);

  // 0.36.37 — Keep the three text inputs in sync with the `value`
  // prop. When the calendar pick fires onChange (or any external
  // source updates value), the three boxes need to reflect it. We
  // skip this sync while a field is focused (the user is mid-edit
  // and we don't want to overwrite their keystrokes), and we only
  // run when the parsed value actually differs from what's in the
  // boxes — avoiding loops where blur → onChange → effect → set
  // inputs → blur again.
  useEffect(() => {
    const active = document.activeElement;
    const userIsTyping =
      active === dayRef.current ||
      active === monthRef.current ||
      active === yearRef.current;
    if (userIsTyping) return;
    if (selectedDate) {
      const d = String(selectedDate.getDate()).padStart(2, '0');
      const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const y = String(selectedDate.getFullYear());
      if (d !== dayInput) setDayInput(d);
      if (m !== monthInput) setMonthInput(m);
      if (y !== yearInput) setYearInput(y);
    } else {
      if (dayInput) setDayInput('');
      if (monthInput) setMonthInput('');
      if (yearInput) setYearInput('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Holidays as a Set for O(1) lookup. Built once per render — cheap
  // since the list is normally a few dozen dates at most.
  const holidaysSet = new Set(
    Array.isArray(holidays)
      ? holidays.filter(s => typeof s === 'string')
      : []
  );

  // 0.35.63 — Disabled-dates Set + min/max bounds parsed once per render.
  const disabledSet = new Set(
    Array.isArray(disabledDates)
      ? disabledDates.filter(s => typeof s === 'string')
      : []
  );
  const minDateObj = fromIsoDateString(minDate);
  const maxDateObj = fromIsoDateString(maxDate);

  // 0.35.71 — Non-working configuration: which weekdays + which
  // specific-date overrides.
  const nonWorkingDaysSet = new Set(
    Array.isArray(nonWorkingDays)
      ? nonWorkingDays.filter(n => Number.isInteger(n) && n >= 0 && n <= 6)
      : [0, 6]
  );
  const workingOverridesSet = new Set(
    Array.isArray(workingOverrides)
      ? workingOverrides.filter(s => typeof s === 'string')
      : []
  );
  const nonWorkingOverridesSet = new Set(
    Array.isArray(nonWorkingOverrides)
      ? nonWorkingOverrides.filter(s => typeof s === 'string')
      : []
  );

  /** Is `d` a "non-working" day — i.e. should it render with the
   *  non-working color? This is a visual hint only; selectability is
   *  governed by isDateDisabled and is independent of this.
   *
   *  Resolution order (first match wins):
   *    1. workingOverrides       → working      (admin says yes)
   *    2. nonWorkingOverrides    → non-working  (admin says no)
   *    3. holidays               → non-working
   *    4. nonWorkingDays         → non-working  (weekday-of-week rule)
   *    5. fallthrough            → working
   *
   *  The working override wins over everything because it's the
   *  most "positive" assertion: an admin explicitly flagging a date
   *  as a workday overrides any general rule that would have made
   *  it non-working. */
  const isNonWorking = (d) => {
    if (!d) return false;
    const iso = toIsoDateString(d);
    if (workingOverridesSet.has(iso))    return false;
    if (nonWorkingOverridesSet.has(iso)) return true;
    if (holidaysSet.has(iso))            return true;
    return nonWorkingDaysSet.has(d.getDay());
  };

  /* 0.35.97 — Pre-parse the disabledRanges array into Date pairs
   * once per render, dropping any entries with bad ISO strings. The
   * isDateDisabled callback then just does a linear scan — fine at
   * configurator-realistic counts (admin will rarely add more than
   * a few dozen blackout ranges). If we ever need to scale to
   * hundreds we can switch to an interval tree. */
  const disabledRangesParsed = (Array.isArray(disabledRanges) ? disabledRanges : [])
    .map(r => ({
      start: fromIsoDateString(r?.start),
      end:   fromIsoDateString(r?.end),
    }))
    .filter(r => r.start && r.end);

  /** A date is "disabled" (not selectable) when it's:
   *   • Listed explicitly in disabledDates, OR
   *   • Before minDate (if set), OR
   *   • After maxDate (if set), OR
   *   • Inside any range in disabledRanges (0.35.97).
   * Weekends and holidays are NOT automatically disabled — they're
   * "non-working" (different visual, still selectable). Disabled is
   * a stronger signal: the user can't choose this date at all. */
  const isDateDisabled = useCallback((d) => {
    if (!d) return false;
    if (disabledSet.has(toIsoDateString(d))) return true;
    if (minDateObj && d < minDateObj) return true;
    if (maxDateObj && d > maxDateObj) return true;
    for (const r of disabledRangesParsed) {
      if (d >= r.start && d <= r.end) return true;
    }
    return false;
  }, [disabledSet, minDateObj, maxDateObj, disabledRangesParsed]);

  /** Advance forward from `start` until we find a non-disabled day,
   *  bounded to 366 attempts (year and a day) so we never loop
   *  forever on a fully-disabled range. Used to anchor keyboard
   *  navigation away from an unselectable starting point. */
  const findFirstSelectableFrom = useCallback((start) => {
    let d = new Date(start);
    for (let i = 0; i < 366; i++) {
      if (!isDateDisabled(d)) return d;
      d.setDate(d.getDate() + 1);
    }
    return start;  // gave up — return original; user gets stuck (rare)
  }, [isDateDisabled]);

  // When `value` changes externally, re-anchor the view to its month
  // (only when the panel is closed, so we don't yank the view out
  // from under a user mid-navigation).
  useEffect(() => {
    if (open) return;
    const parsed = fromIsoDateString(value);
    if (parsed) setViewDate(parsed);
  }, [value, open]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // 0.44.63 - Escape closes from anywhere on the page; focus returns
  // to the calendar button when it was inside the picker.
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      const inside = containerRef.current && containerRef.current.contains(document.activeElement);
      setOpen(false);
      if (inside && calendarBtnRef.current) calendarBtnRef.current.focus();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  // 0.44.63 - Roving tabindex: after a keyboard move (or on opening),
  // focus follows the highlighted cell. A click on a header button is
  // left alone: focus moves only from a cell, or when the popover opens.
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const active = document.activeElement;
    const fromCell = active && active.dataset && active.dataset.cell;
    if (!wantFocusRef.current && !fromCell) return;
    const target = panelRef.current.querySelector('[data-roving="1"]');
    if (target) {
      target.focus();
      wantFocusRef.current = false;
    }
  }, [open, viewMode, viewDate, keyboardDate, keyboardYear, keyboardMonth, yearViewStart]);

  // Keys on a cell of the open popover (the header buttons keep their
  // own Enter / Space).
  const handlePanelKeyDown = (e) => {
    const cell = e.target && e.target.dataset ? e.target.dataset.cell : '';
    if (!cell) return;
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown',
                  'Home', 'End', 'Enter', ' '];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    // 0.44.64 - The cell to focus may be a new element (another page of
    // years, another month), so ask for focus explicitly.
    wantFocusRef.current = true;
    if (viewMode === 'days') {
      const cur = keyboardDate || findFirstSelectableFrom(selectedDate || today);
      if (e.key === 'Enter' || e.key === ' ') { pickDate(cur, true); return; }
      const next = new Date(cur);
      if (e.key === 'ArrowLeft')       next.setDate(next.getDate() - 1);
      else if (e.key === 'ArrowRight') next.setDate(next.getDate() + 1);
      else if (e.key === 'ArrowUp')    next.setDate(next.getDate() - 7);
      else if (e.key === 'ArrowDown')  next.setDate(next.getDate() + 7);
      else if (e.key === 'PageUp')     next.setMonth(next.getMonth() - 1);
      else if (e.key === 'PageDown')   next.setMonth(next.getMonth() + 1);
      else if (e.key === 'Home') next.setDate(next.getDate() - weekdayIndex(next, weekStart));
      else if (e.key === 'End')  next.setDate(next.getDate() + (6 - weekdayIndex(next, weekStart)));
      setKeyboardDate(next);
      if (next.getFullYear() !== viewDate.getFullYear() || next.getMonth() !== viewDate.getMonth()) {
        setViewDate(new Date(next.getFullYear(), next.getMonth(), 1));
      }
    } else if (viewMode === 'years') {
      const cur = keyboardYear != null ? keyboardYear : (selectedDate || today).getFullYear();
      if (e.key === 'Enter' || e.key === ' ') { pickYear(cur); return; }
      let next = cur;
      if (e.key === 'ArrowLeft') next -= 1;
      else if (e.key === 'ArrowRight') next += 1;
      else if (e.key === 'ArrowUp') next -= 3;
      else if (e.key === 'ArrowDown') next += 3;
      else if (e.key === 'PageUp') next -= YEARS_PER_PAGE;
      else if (e.key === 'PageDown') next += YEARS_PER_PAGE;
      else if (e.key === 'Home') next = yearViewStart;
      else if (e.key === 'End') next = yearViewStart + YEARS_PER_PAGE - 1;
      setKeyboardYear(next);
      if (next < yearViewStart || next > yearViewStart + YEARS_PER_PAGE - 1) setYearViewStart(yearPageStart(next));
    } else if (viewMode === 'months') {
      const cur = keyboardMonth || new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
      if (e.key === 'Enter' || e.key === ' ') { pickMonth(cur.getMonth()); return; }
      const next = new Date(cur);
      if (e.key === 'ArrowLeft')       next.setMonth(next.getMonth() - 1);
      else if (e.key === 'ArrowRight') next.setMonth(next.getMonth() + 1);
      else if (e.key === 'ArrowUp')    next.setMonth(next.getMonth() - 3);
      else if (e.key === 'ArrowDown')  next.setMonth(next.getMonth() + 3);
      else if (e.key === 'PageUp')     next.setFullYear(next.getFullYear() - 1);
      else if (e.key === 'PageDown')   next.setFullYear(next.getFullYear() + 1);
      else if (e.key === 'Home') next.setMonth(0);
      else if (e.key === 'End') next.setMonth(11);
      setKeyboardMonth(next);
      if (next.getFullYear() !== viewDate.getFullYear()) {
        setViewDate(new Date(next.getFullYear(), viewDate.getMonth(), 1));
      }
    }
  };

  // Tabbing out of the picker closes the popover (focus is not pulled back).
  const handlePanelBlur = (e) => {
    const to = e.relatedTarget;
    if (to && containerRef.current && containerRef.current.contains(to)) return;
    if (to) setOpen(false);
  };

  const openPanel = useCallback(() => {
    if (disabled) return;
    const wantedAnchor = selectedDate || new Date();
    const usableAnchor = findFirstSelectableFrom(wantedAnchor);
    setViewDate(usableAnchor);
    setKeyboardDate(usableAnchor);
    setKeyboardYear(null);
    setKeyboardMonth(null);
    setViewMode('days');
    wantFocusRef.current = true;
    setOpen(true);
  }, [disabled, selectedDate, findFirstSelectableFrom]);

  const closePanel = useCallback(() => setOpen(false), []);

  const handleTriggerClick = () => {
    if (disabled) return;
    if (open) closePanel();
    else openPanel();
  };

  // 0.36.37 — Handlers for the structured DD / MM / YYYY input.
  //
  // Typing rules:
  //   - digits only (non-digit input filtered out before state update)
  //   - day/month max 2 chars; year max 4
  //   - auto-advance: hitting the maxLength fires focus on the next box
  //   - backspace at the start of an empty box jumps focus back
  //
  // Validation rules:
  //   - happens on blur of any of the three inputs
  //   - if all three parse to a real date (and year is 4 digits), fire
  //     onChange with the ISO string
  //   - if any field is incomplete or invalid, do nothing — leave the
  //     user's text in place; the parent's `value` stays as it was
  const onlyDigits = (s) => s.replace(/\D/g, '');

  // 0.44.65 - The next / previous box in the platform's order.
  const partRefs = { day: dayRef, month: monthRef, year: yearRef };
  const focusPart = (part, dir) => {
    const next = partOrder[partOrder.indexOf(part) + dir];
    const ref = next && partRefs[next];
    if (ref && ref.current) { ref.current.focus(); if (dir > 0) ref.current.select(); return true; }
    return false;
  };
  const handleDayChange = (e) => {
    const v = onlyDigits(e.target.value).slice(0, 2);
    setDayInput(v);
    if (v.length === 2) focusPart('day', 1);
  };
  const handleMonthChange = (e) => {
    const v = onlyDigits(e.target.value).slice(0, 2);
    setMonthInput(v);
    if (v.length === 2) focusPart('month', 1);
  };
  const handleYearChange = (e) => {
    const v = onlyDigits(e.target.value).slice(0, 4);
    setYearInput(v);
    if (v.length === 4) focusPart('year', 1);
  };

  const handlePartKeyDown = (field) => (e) => {
    // Backspace at the start of an empty (or about-to-be-empty) field
    // moves focus to the previous field. Standard segmented-input UX.
    if (e.key === 'Backspace' && e.target.value === '') {
      if (focusPart(field, -1)) e.preventDefault();
    }
  };

  const tryCommitTypedDate = () => {
    // Called on blur of any of the three inputs. Checks if all three
    // are filled and form a real date — and only then propagates.
    if (!dayInput || !monthInput || yearInput.length !== 4) return;
    const d = parseInt(dayInput, 10);
    const m = parseInt(monthInput, 10);
    const y = parseInt(yearInput, 10);
    if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) return;
    if (m < 1 || m > 12) return;
    if (d < 1 || d > 31) return;
    // Build a Date and check it round-trips (catches Feb 30 etc. —
    // JS Date silently rolls over, so Mar 2 would come back as
    // (3, 2), not (2, 30); we reject when getMonth/getDate disagree
    // with what we asked for).
    const candidate = new Date(y, m - 1, d);
    if (
      candidate.getFullYear() !== y ||
      candidate.getMonth() !== m - 1 ||
      candidate.getDate() !== d
    ) return;
    const iso = toIsoDateString(candidate);
    if (iso !== value) onChange?.(iso);
  };


  const pickDate = (d) => {
    if (isDateDisabled(d)) return;       // ignore disabled clicks / Enter
    onChange?.(toIsoDateString(d));
    closePanel();
    // 0.44.63 - Back to the calendar button, so the keyboard user is
    // where the popover was opened from.
    if (calendarBtnRef.current) calendarBtnRef.current.focus();
  };
  const prevYear = () => setViewDate(new Date(viewDate.getFullYear() - 1, viewDate.getMonth(), 1));
  const nextYear = () => setViewDate(new Date(viewDate.getFullYear() + 1, viewDate.getMonth(), 1));

  const prevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  // ---- Year-picker (viewMode='years') handlers ----

  const enterYearMode = () => {
    wantFocusRef.current = true;   // 0.44.64 - focus follows into the new view
    // 0.35.68 — Anchor: 9 years before view year. With a 20-year
    // grid, this puts the view year roughly in the middle row.
    setYearViewStart(yearPageStart(viewDate.getFullYear()));
    // Start keyboard nav on the selected year, or today if none.
    setKeyboardYear(selectedDate
      ? selectedDate.getFullYear()
      : today.getFullYear());
    setViewMode('years');
  };

  const exitYearMode = () => {
    wantFocusRef.current = true;   // 0.44.64 - focus follows into the new view
    setKeyboardYear(null);
    setViewMode('days');
  };

  const pickYear = (year) => {
    wantFocusRef.current = true;   // 0.44.64 - focus follows into the new view
    // 0.35.69 — After picking a year, drop into MONTH mode (was: go
    // straight back to days). The user's flow is now:
    //   header → years → months → days.
    const newViewDate = new Date(year, viewDate.getMonth(), 1);
    setViewDate(newViewDate);
    setKeyboardYear(null);
    // Anchor month-picker keyboard on the same month we were viewing.
    setKeyboardMonth(new Date(year, viewDate.getMonth(), 1));
    setViewMode('months');
  };

  // ---- Month-picker (viewMode='months') handlers (0.35.69) ----

  /** Going back from month → year picker. Re-anchors the year window
   *  on the currently-viewing year, restores the keyboard-year cursor. */
  const exitMonthMode = () => {
    wantFocusRef.current = true;   // 0.44.64 - focus follows into the new view
    setYearViewStart(yearPageStart(viewDate.getFullYear()));
    setKeyboardYear(viewDate.getFullYear());
    setKeyboardMonth(null);
    setViewMode('years');
  };

  /** Picking a month — jump days view to that year+month, find the
   *  first selectable day to anchor keyboard nav on, and switch to
   *  days mode. */
  const pickMonth = (monthIdx) => {
    wantFocusRef.current = true;   // 0.44.64 - focus follows into the new view
    const firstOfMonth = new Date(viewDate.getFullYear(), monthIdx, 1);
    setViewDate(firstOfMonth);
    setKeyboardDate(findFirstSelectableFrom(firstOfMonth));
    setKeyboardMonth(null);
    setViewMode('days');
  };

  // 0.35.68 — page by 20 to match the new 20-year grid.
  const prevYearPage = () => setYearViewStart(y => y - YEARS_PER_PAGE);
  const nextYearPage = () => setYearViewStart(y => y + YEARS_PER_PAGE);

  // The ‹ › buttons context-switch: in days mode they page by
  // month, in years mode they page by 12 years.
  // The ‹ › buttons context-switch:
  //   • days mode   → page by 1 month
  //   • years mode  → page by 20 years
  //   • months mode → page by 1 year (the 12-month grid stays the
  //                   same; only the year being shown changes).
  const handlePrev = () => {
    if (viewMode === 'days') prevMonth();
    else if (viewMode === 'months') {
      const newYear = viewDate.getFullYear() - 1;
      setViewDate(new Date(newYear, viewDate.getMonth(), 1));
      if (keyboardMonth) {
        setKeyboardMonth(new Date(newYear, keyboardMonth.getMonth(), 1));
      }
    }
    else prevYearPage();
  };
  const handleNext = () => {
    if (viewMode === 'days') nextMonth();
    else if (viewMode === 'months') {
      const newYear = viewDate.getFullYear() + 1;
      setViewDate(new Date(newYear, viewDate.getMonth(), 1));
      if (keyboardMonth) {
        setKeyboardMonth(new Date(newYear, keyboardMonth.getMonth(), 1));
      }
    }
    else nextYearPage();
  };

  const grid = buildGrid(viewDate.getFullYear(), viewDate.getMonth(), weekStart);

  // 0.44.63 - Localized month and weekday names (translation layer,
  // with built-in defaults); the weekday row starts on weekStart.
  const monthYearLabel = `${months[viewDate.getMonth()]} ${viewDate.getFullYear()}`;
  const weekdayInfo = Array.from({ length: 7 }, (_, i) => {
    const dayOfWeek = (weekStart + i) % 7;
    return {
      label: weekdaysMonFirst[(dayOfWeek + 6) % 7],
      isNonWorking: nonWorkingDaysSet.has(dayOfWeek),
    };
  });

  // 0.35.63 — if the caller passed a custom non-working color, expose
  // it as a CSS custom property on the field root. The .dayNonWorking
  // and .weekdayWeekend rules use var(--henaket-non-working-color, …)
  // with a fallback so the default still applies when this prop is
  // not set.
  const fieldStyle = nonWorkingColor
    ? { '--henaket-non-working-color': nonWorkingColor }
    : undefined;

  const dayId = `${baseId}-day`;
  const monthId = `${baseId}-month`;
  const yearId = `${baseId}-year`;
  // The one day in the tab order: the keyboard's day when it is in the
  // month shown, else the selected day, else the first selectable day
  // of that month (so paging with < > still leaves a day to Tab to).
  const inView = (d) => d && d.getFullYear() === viewDate.getFullYear() && d.getMonth() === viewDate.getMonth();
  const rovingDate = inView(keyboardDate) ? keyboardDate
    : inView(selectedDate) ? selectedDate
      : findFirstSelectableFrom(new Date(viewDate.getFullYear(), viewDate.getMonth(), 1));

  return (
    <div className={`${styles.field} ${flush ? styles.flush : ''}`} style={fieldStyle}>
      {/* 0.44.63 - A group named by its label (the specification's
          fieldset/legend never named the group: the legend was wrapped
          in a div). */}
      <div
        ref={containerRef}
        role="group"
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : tr('calendar')}
        className={`${styles.container} ${error ? 'field-error-bar' : ''}`}
      >
        {label && (
          <label id={labelId} htmlFor={dayId} className={styles.label}>
            {required && requiredPosition === 'before' && (
              <span className={styles.req} aria-hidden="true">*</span>
            )}
            {label}
            {required && requiredPosition === 'after' && (
              <span className={styles.req} aria-hidden="true">*</span>
            )}
          </label>
        )}
        {/* A message, when the error is one (a bare true only draws the bar). */}
        {typeof error === 'string' && error && <div className="field-error-text">{error}</div>}

        <div className={styles.dateRow}>
          {partOrder.map((part, i) => (
            <Fragment key={part}>
              {i > 0 && <span className={styles.dateSeparator} aria-hidden="true">/</span>}
              {part === 'day' && (
          <div className={`${styles.datePart} ${styles.partDay}`}>
            <label htmlFor={dayId} className={styles.partLabel}>{tr('day')}</label>
            <input
              id={dayId} ref={dayRef} type="text" inputMode="numeric" autoComplete="off"
              className={styles.dateInput} value={dayInput}
              onChange={handleDayChange} onBlur={tryCommitTypedDate}
              onKeyDown={handlePartKeyDown('day')}
              disabled={disabled} maxLength={2} aria-invalid={!!error}
            />
          </div>
              )}
              {part === 'month' && (
          <div className={`${styles.datePart} ${styles.partMonth}`}>
            <label htmlFor={monthId} className={styles.partLabel}>{tr('month')}</label>
            <input
              id={monthId} ref={monthRef} type="text" inputMode="numeric" autoComplete="off"
              className={styles.dateInput} value={monthInput}
              onChange={handleMonthChange} onBlur={tryCommitTypedDate}
              onKeyDown={handlePartKeyDown('month')}
              disabled={disabled} maxLength={2} aria-invalid={!!error}
            />
          </div>
              )}
              {part === 'year' && (
          <div className={`${styles.datePart} ${styles.partYear}`}>
            <label htmlFor={yearId} className={styles.partLabel}>{tr('year')}</label>
            <input
              id={yearId} ref={yearRef} type="text" inputMode="numeric" autoComplete="off"
              className={styles.dateInput} value={yearInput}
              onChange={handleYearChange} onBlur={tryCommitTypedDate}
              onKeyDown={handlePartKeyDown('year')}
              disabled={disabled} maxLength={4} aria-invalid={!!error}
            />
          </div>
              )}
            </Fragment>
          ))}
          <button
            ref={calendarBtnRef}
            type="button"
            className={styles.calendarButton}
            onClick={handleTriggerClick}
            disabled={disabled}
            aria-label={tr('open_calendar')}
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <CalendarIcon />
          </button>
        </div>

        {/* Hidden form-field shim - preserves the API contract. */}
        {name && <input type="hidden" name={name} value={value || ''} readOnly />}

        {open && (
          <div
            ref={panelRef}
            className={styles.panel}
            role="dialog"
            aria-label={tr('calendar')}
            onKeyDown={handlePanelKeyDown}
            onBlur={handlePanelBlur}
          >
            {/* 0.44.64 - Years and months: the design's grey header band. */}
            <div className={`${styles.header} ${viewMode === 'days' ? '' : styles.headerPick}`}>
              {viewMode === 'days' ? (
                <button type="button" className={styles.navButton} onClick={prevYear}
                        aria-label={tr('prev_year')}>&#171;</button>
              ) : null}
              <button type="button" className={styles.navButton} onClick={handlePrev}
                      aria-label={viewMode === 'days' ? tr('prev_month')
                        : viewMode === 'months' ? tr('prev_year') : tr('prev_year_page')}>&#8249;</button>
              <span className={styles.titleWrap} aria-live="polite">
                {viewMode === 'days' && (
                  <button type="button" className={styles.title} onClick={enterYearMode}
                          title={tr('choose_year')}>{monthYearLabel}</button>
                )}
                {viewMode === 'months' && (
                  <button type="button" className={styles.title} onClick={exitMonthMode}
                          title={tr('choose_year')}>{viewDate.getFullYear()}</button>
                )}
                {viewMode === 'years' && (
                  <button type="button" className={styles.title} onClick={exitYearMode}>
                    {yearViewStart} - {yearViewStart + YEARS_PER_PAGE - 1}
                  </button>
                )}
              </span>
              <button type="button" className={styles.navButton} onClick={handleNext}
                      aria-label={viewMode === 'days' ? tr('next_month')
                        : viewMode === 'months' ? tr('next_year') : tr('next_year_page')}>&#8250;</button>
              {viewMode === 'days' ? (
                <button type="button" className={styles.navButton} onClick={nextYear}
                        aria-label={tr('next_year')}>&#187;</button>
              ) : null}
            </div>

            {viewMode === 'days' && (
              <div className={styles.grid} role="grid" aria-label={monthYearLabel}>
                {weekdayInfo.map((info, i) => (
                  <div key={`w${i}`} role="columnheader"
                       className={`${styles.weekday} ${info.isNonWorking ? styles.weekdayWeekend : ''}`}>
                    {info.label}
                  </div>
                ))}
                {grid.map((cell, i) => {
                  if (cell.blank) return <div key={`b${i}`} className={styles.blank} aria-hidden="true" />;
                  const cellDisabled = isDateDisabled(cell.date);
                  const isSel = sameDay(cell.date, selectedDate);
                  const classes = [styles.day];
                  if (isNonWorking(cell.date)) classes.push(styles.dayNonWorking);
                  if (sameDay(cell.date, today)) classes.push(styles.dayToday);
                  if (isSel) classes.push(styles.daySelected);
                  if (cellDisabled) classes.push(styles.dayDisabled);
                  const roving = sameDay(cell.date, rovingDate);
                  return (
                    <button
                      key={`d${i}`}
                      type="button"
                      data-cell="day"
                      data-roving={roving ? '1' : undefined}
                      tabIndex={roving ? 0 : -1}
                      className={classes.join(' ')}
                      onClick={() => pickDate(cell.date)}
                      onFocus={() => { if (!sameDay(cell.date, keyboardDate)) setKeyboardDate(cell.date); }}
                      aria-pressed={isSel}
                      aria-disabled={cellDisabled || undefined}
                      aria-current={sameDay(cell.date, today) ? 'date' : undefined}
                      aria-label={`${cell.date.getDate()} ${months[cell.date.getMonth()]} ${cell.date.getFullYear()}`}
                    >
                      {cell.date.getDate()}
                    </button>
                  );
                })}
              </div>
            )}

            {viewMode === 'years' && (
              <div className={`${styles.pickGrid} ${styles.pickYears}`}>
                {Array.from({ length: YEARS_PER_PAGE }, (_, i) => {
                  const yearValue = yearViewStart + i;
                  const roving = yearValue === (keyboardYear != null ? keyboardYear : viewDate.getFullYear());
                  const classes = [styles.pickCell];
                  if (yearValue === today.getFullYear()) classes.push(styles.dayToday);
                  if (selectedDate && yearValue === selectedDate.getFullYear()) classes.push(styles.daySelected);
                  return (
                    <button key={yearValue} type="button" data-cell="year"
                            data-roving={roving ? '1' : undefined} tabIndex={roving ? 0 : -1}
                            className={classes.join(' ')} onClick={() => pickYear(yearValue)}
                            onFocus={() => { if (keyboardYear !== yearValue) setKeyboardYear(yearValue); }}
                            aria-pressed={!!(selectedDate && yearValue === selectedDate.getFullYear())}>
                      {yearValue}
                    </button>
                  );
                })}
              </div>
            )}

            {viewMode === 'months' && (
              <div className={`${styles.pickGrid} ${styles.pickMonths}`}>
                {Array.from({ length: 12 }, (_, i) => {
                  const isSelected = !!(selectedDate && selectedDate.getFullYear() === viewDate.getFullYear()
                    && selectedDate.getMonth() === i);
                  const isToday = today.getFullYear() === viewDate.getFullYear() && today.getMonth() === i;
                  const km = keyboardMonth || new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
                  const roving = km.getFullYear() === viewDate.getFullYear() && km.getMonth() === i;
                  const classes = [styles.pickCell];
                  if (isToday) classes.push(styles.dayToday);
                  if (isSelected) classes.push(styles.daySelected);
                  return (
                    <button key={i} type="button" data-cell="month"
                            data-roving={roving ? '1' : undefined} tabIndex={roving ? 0 : -1}
                            className={classes.join(' ')} onClick={() => pickMonth(i)}
                            onFocus={() => { if (!roving) setKeyboardMonth(new Date(viewDate.getFullYear(), i, 1)); }}
                            aria-pressed={isSelected}>
                      {months[i]}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg
      className={styles.icon}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="16" y1="2" x2="16" y2="6" />
    </svg>
  );
}
